import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdClose } from 'react-icons/md';
import useTheme from '../../context/ThemeContext';
import { useCalendar } from '../../context/CalendarContext';
import useClubVocab from '../../hooks/useClubVocab';
import { giveDiscount, getAllPayments, getFeeStructure } from '../../services/feeService';
import { adHint } from '../../utils/calendar';

// Asia/Kathmandu (UTC+5:45) current month. adHint (muted AD-month hint) lives in utils/calendar.
const KTH = (5 * 60 + 45) * 60 * 1000;
const kthNow = () => new Date(Date.now() + KTH);
const addMonths = (ym, n) => {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

// Radio dot for the single-select month rows.
function Radio({ on }) {
  return (
    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${on ? 'border-secondary' : 'border-divider'}`}>
      {on && <span className="w-2 h-2 rounded-full bg-secondary" />}
    </span>
  );
}

// One selectable month row: month (BS · AD) on the left, amount on the right. Upcoming rows
// add a muted "not billed yet" note under the fee.
function MonthRow({ m, kind, on, onSelect, formatMonth }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full min-h-[44px] flex items-center justify-between gap-3 px-3 py-2 rounded-xl border text-left transition-colors ${
        on ? 'border-secondary bg-nav-hover' : 'border-divider hover:border-secondary'
      }`}
    >
      <span className="flex items-center gap-2.5 min-w-0">
        <Radio on={on} />
        <span className="text-sm text-secondary truncate">{formatMonth(m.month)}<span className="text-muted"> · {adHint(m.month)}</span></span>
      </span>
      <span className="text-right whitespace-nowrap flex-shrink-0">
        <span className="block text-sm font-medium text-secondary">NPR {m.gross.toLocaleString()}</span>
        {kind === 'upcoming' && <span className="block text-[10px] text-muted">not billed yet</span>}
      </span>
    </button>
  );
}

// Per-student, per-month discount. Months are shown as a grouped single-select list — "Unpaid"
// (existing pending/overdue fee docs) and "Upcoming" (current/next-2, not yet billed). Amount
// must be > 0 and ≤ that month's owed total.
export default function GiveDiscountModal({ studentId, onClose, onCreated }) {
  const currentTheme = useTheme()?.currentTheme;
  const { formatMonth } = useCalendar();
  const v = useClubVocab();
  const [month, setMonth]   = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote]     = useState('');
  const [error, setError]   = useState('');

  const { data: unpaidData } = useQuery({
    queryKey: ['student-unpaid-months', studentId],
    queryFn:  () => getAllPayments({ studentId, status: 'pending,overdue', limit: 100 }).then(r => r.data),
    enabled:  !!studentId,
  });
  const { data: structureData } = useQuery({
    queryKey: ['fee-structure'],
    queryFn:  () => getFeeStructure().then(r => r.data),
  });
  const baseFee = Number(structureData?.structure?.monthlyFee || 0);

  // UNPAID: months with an existing pending/overdue monthly fee doc. Gross = base + stamped
  // late fee (ignores any discount already on them).
  const unpaid = (unpaidData?.payments || unpaidData?.data || []).filter(p => (p.type || 'monthly') !== 'custom');
  const unpaidByMonth = new Map();
  for (const p of unpaid) unpaidByMonth.set(p.month, (unpaidByMonth.get(p.month) || 0) + ((p.amount || 0) + (p.lateFee || 0)));
  const unpaidMonths = [...unpaidByMonth.entries()]
    .map(([m, gross]) => ({ month: m, gross }))
    .sort((a, b) => (a.month < b.month ? -1 : 1));

  // UPCOMING: current + next two Kathmandu months not yet billed. Gross = structure/override base.
  const cur = `${kthNow().getUTCFullYear()}-${String(kthNow().getUTCMonth() + 1).padStart(2, '0')}`;
  const upcomingMonths = [cur, addMonths(cur, 1), addMonths(cur, 2)]
    .filter(mo => !unpaidByMonth.has(mo))
    .map(mo => ({ month: mo, gross: baseFee }));

  const noMonths = unpaidMonths.length === 0 && upcomingMonths.length === 0;
  const owed     = ([...unpaidMonths, ...upcomingMonths].find(m => m.month === month)?.gross) || 0;
  const num      = Number(amount);
  const payable  = Math.max(0, owed - (num > 0 ? num : 0));
  const showBreakdown = !!month && num > 0 && num <= owed;
  const selectRow = (mo) => { setMonth(mo); if (error) setError(''); };

  const mutation = useMutation({
    mutationFn: () => giveDiscount({ studentId, month, amount: num, note: note.trim() }),
    onSuccess:  () => { toast.success('Discount applied!'); onCreated?.(); onClose?.(); },
    onError:    (e) => setError(e.response?.data?.message || 'Could not apply the discount. Please try again.'),
  });

  const submit = (e) => {
    e.preventDefault();
    setError('');
    if (!month)          { setError('Please select a month.'); return; }
    if (!num || num <= 0) { setError('Enter a discount amount greater than 0.'); return; }
    if (num > owed)      { setError(`Discount cannot exceed the amount owed (NPR ${owed.toLocaleString()}).`); return; }
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <form onSubmit={submit} className="bg-card border border-divider rounded-2xl shadow-2xl w-full max-w-sm max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider flex-shrink-0">
          <h3 className="font-bold text-secondary">Give Discount</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-muted hover:text-secondary hover:bg-nav-hover"><MdClose size={20} /></button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          <p className="text-xs text-muted">The discount will be cut from the selected month&apos;s fee. Reminders will show the reduced amount.</p>

          {/* Month — grouped single-select list */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Month <span className="text-red-400">*</span></label>
            {noMonths ? (
              <div className="rounded-xl border border-divider bg-nav-hover px-4 py-6 text-center">
                <p className="text-sm text-muted">This {v.member.toLowerCase()} has no months eligible for a discount.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {unpaidMonths.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted">Unpaid</p>
                    <div className="space-y-1.5">
                      {unpaidMonths.map(m => (
                        <MonthRow key={m.month} m={m} kind="unpaid" on={m.month === month} onSelect={() => selectRow(m.month)} formatMonth={formatMonth} />
                      ))}
                    </div>
                  </div>
                )}
                {upcomingMonths.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-muted">Upcoming</p>
                    <div className="space-y-1.5">
                      {upcomingMonths.map(m => (
                        <MonthRow key={m.month} m={m} kind="upcoming" on={m.month === month} onSelect={() => selectRow(m.month)} formatMonth={formatMonth} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Amount + note appear once a month is chosen */}
          {month && (
          <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Discount amount (NPR) <span className="text-red-400">*</span></label>
            <input
              type="number" min="1"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); if (error) setError(''); }}
              placeholder="e.g. 500"
              className="w-full min-h-[44px] px-3 py-2.5 rounded-xl text-sm bg-card border border-divider text-secondary placeholder:text-subtle focus:outline-none focus:border-secondary transition-colors"
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs font-medium text-muted mb-1">Note (optional)</label>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. sibling discount"
              className="w-full min-h-[44px] px-3 py-2.5 rounded-xl text-sm bg-card border border-divider text-secondary placeholder:text-subtle focus:outline-none focus:border-secondary transition-colors"
            />
          </div>

          {/* Confirm breakdown */}
          {showBreakdown && (
            <div className="bg-nav-hover rounded-xl px-4 py-3 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted">Original</span>
                <span className="text-xs text-muted line-through">NPR {owed.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-emerald-500">Discount</span>
                <span className="text-xs text-emerald-500">− NPR {num.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between pt-1.5 border-t border-divider">
                <span className="text-sm font-medium text-muted">New payable</span>
                <span className="text-base font-bold text-secondary">NPR {payable.toLocaleString()}</span>
              </div>
            </div>
          )}
          </div>
          )}

          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>

        <div className="flex gap-3 px-5 pb-5 pt-1 flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 min-h-[44px] px-4 py-2.5 rounded-full border border-divider text-sm font-medium text-secondary hover:bg-secondary hover:text-card hover:border-secondary transition-all duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className={`flex-1 min-h-[44px] px-4 py-2.5 rounded-full text-sm font-medium ${currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card'} hover:opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {mutation.isPending ? 'Applying…' : 'Apply Discount'}
          </button>
        </div>
      </form>
    </div>
  );
}
